import React, { useEffect, useRef, useState } from "react";
import Modal from "@mui/joy/Modal";
import ModalDialog from "@mui/joy/ModalDialog";
import { add_brand, edit_brand, get_brand } from "../../reduxData/user/userAction";
import EmptyList from "../../Shared/EmptyList";
import { useDispatch, useSelector } from "react-redux";
import CustomPagination from "../../components/CustomPagination";
import { useTranslation } from "react-i18next";
import { Pause, Play, Search, SlidersHorizontal, X } from "lucide-react";

const Brand = () => {
    const { t } = useTranslation();
    const [open, setOpen] = React.useState(false);
    const { userPermissions } = useSelector((state) => state.auth);
    const { brandList, totalbrands } = useSelector((state) => state.user);
    const [selectedBrandId, setSelectedBrandId] = useState(null);
    const [brandName, setBrandName] = useState(null);
    const [editId, setEditId] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [page, setPage] = useState(1);
    const [statusFilter, setStatusFilter] = useState("All");
    const [perPage, setPerPage] = useState(10);
    const [resetPage, setResetPage] = useState(false);
    const [errors, setErrors] = useState({
        name: "",
    });
    const [name, setName] = useState("");
    const dispatch = useDispatch();
    const [approvalStatus, setApprovalStatus] = useState(null);
    const canCreateBrand = userPermissions?.brand?.create;
    const canEditBrand = userPermissions?.brand?.edit;
    const canToggleBrand = userPermissions?.brand?.delete;
    const canManageBrand = canEditBrand || canToggleBrand;

    // console.log("brandList", brandList);

    // fetch brand data 
    const fetchData = async (page = currentPage, rows = perPage) => {
        try {
            await get_brand(dispatch, page, rows, name, statusFilter);
        } catch (error) {
            console.error("Error fetching category data:", error);
        }
    };

    useEffect(() => {
        fetchData();
    }, [currentPage, perPage, name, statusFilter]);

    const setEditCategory = (edit) => {
        setBrandName(edit?.name);
        setEditId(edit?.id);
        const derivedStatus = (edit?.approval_status || edit?.status || (typeof edit?.admin_approval !== "undefined" ? (edit?.admin_approval ? "APPROVED" : "PENDING") : null));
        setApprovalStatus(derivedStatus ? String(derivedStatus).toUpperCase() : "PENDING");
        setOpen(true);
    };

    // handle brand name value change 
    const handleChange = (e, label) => {
        if (label == "name") {
            if (!e.target.value) {
                setErrors({ ...errors, [label]: t("brand.brandNameRequired") });
            } else {
                setErrors({ ...errors, [label]: "" });
            }
        }
    };

    // handle form submit 
    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrors({
            name: !brandName ? t("brand.brandNameRequired") : null,
        });
        if (!brandName) {
            return;
        }
        if (editId) {
            const formData = { name: brandName };
            if (approvalStatus) {
                formData.approval_status = approvalStatus;
                formData.status = approvalStatus;
                if (approvalStatus === "APPROVED") formData.admin_approval = true;
                else if (approvalStatus === "REJECTED") formData.admin_approval = false;
            }
            const data = await edit_brand(formData, editId, dispatch);
            if (data) {
                fetchData();
                setOpen(false);
                setApprovalStatus(null);
            }
        } else if (!editId) {
            const formData = { name: brandName };
            const data = await add_brand(formData, dispatch);
            if (data) {
                fetchData();
                setOpen(false);
                setApprovalStatus(null);
            }
        }
    };

    // hanlde brand deative 
    const handleDelete = async (item) => {
        const itemchange_active = item?.is_active ? false : true
        const ActiveformData = { name: item?.name, is_active: itemchange_active };
        const data = await edit_brand(ActiveformData, item?.id, dispatch);
        if (data) {
            fetchData();
        }
    };

    const statusClassMap = {
        true: 'approved',
        false: 'cancelled',
        APPROVED: 'approved',
        REJECTED: 'cancelled',
        PENDING: 'pending',
    };

    const clearFilters = () => {
        setName("");
        setStatusFilter("All");
        setCurrentPage(1);
    };

    return (
        <div className="brand-page">
            <div className="brand-header">
                <div>
                    <h2>{t("brand.pageTitle")}</h2>
                    <p>{t("brand.pageSubtitle")}</p>
                </div>
                {canCreateBrand && (
                    <button
                        className="primary-btn"
                        onClick={() => {
                            setEditId(null);
                            setBrandName(null);
                            setApprovalStatus(null);
                            setOpen(true);
                        }}
                    >
                        {t("brand.addNewBrand")}
                    </button>
                )}
            </div>

            <Modal
                open={open}
                onClose={() => {
                    setOpen(false);
                    setErrors({ name: "" });
                    setApprovalStatus(null);
                }}
            >
                <ModalDialog className="modern-modal small-modal brand-modal">
                    <div className="modal-header-custom">
                        <h3>{editId ? t("brand.modalEditTitle") : t("brand.modalCreateTitle")}</h3>
                        <button className="close-btn" type="button" onClick={() => setOpen(false)}>✕</button>
                    </div>
                    <form onSubmit={handleSubmit}>
                        <div className="modal-body-custom brand-modal-body">
                            <div className="form-group-modern">
                                <label htmlFor="brandName">{t("brand.brandNameLabel")}</label>
                                <input
                                    id="brandName"
                                    className={`modern-input ${errors?.name ? "input-error" : ""}`}
                                    autoFocus
                                    placeholder={t("brand.brandNamePlaceholder")}
                                    value={brandName || ""}
                                    onChange={(e) => {
                                        setBrandName(e.target.value);
                                        handleChange(e, "name");
                                    }}
                                />
                                {errors?.name && (
                                    <div className="error_msg">{errors?.name}</div>
                                )}
                            </div>
                            {editId && approvalStatus && (
                                <div className="form-group-modern">
                                    <label htmlFor="brandStatus">{t("brand.brandStatusLabel")}</label>
                                    <select id="brandStatus" className="modern-input brand-modal-select"
                                        value={approvalStatus}
                                        onChange={(e) => setApprovalStatus(e.target.value)}>
                                        <option value="PENDING">{t("brand.statusPending")}</option>
                                        <option value="APPROVED">{t("brand.statusApproved")}</option>
                                        <option value="REJECTED">{t("brand.statusRejected")}</option>
                                    </select>
                                </div>
                            )}
                        </div>
                        <div className="modal-footer-custom">
                            <button type="button" className="secondary-btn" onClick={() => setOpen(false)}>
                                {t("brand.cancel")}
                            </button>
                            <button type="submit" className="primary-btn">
                                {t("brand.save")}
                            </button>
                        </div>
                    </form>
                </ModalDialog>
            </Modal>

            <section className="category-filter-card">
                <div className="category-filter-heading">
                    <span className="category-filter-icon" aria-hidden="true">
                        <SlidersHorizontal size={17} />
                    </span>
                    <div>
                        <strong>{t("brand.findBrands")}</strong>
                        <small>{t("brand.filterHint")}</small>
                    </div>
                </div>

                <div className="category-filter-controls">
                    <div className="category-filter-search">
                        <Search size={17} aria-hidden="true" />
                        <input
                            id="nameInput"
                            type="search"
                            aria-label={t("brand.searchPlaceholder")}
                            placeholder={t("brand.searchPlaceholder")}
                            value={name}
                            onChange={(e) => {
                                setName(e.target.value);
                                setCurrentPage(1);
                            }}
                        />
                    </div>

                    <label className="category-filter-field">
                        <span>{t("brand.viewByStatus")}</span>
                        <select
                            value={statusFilter}
                            onChange={(e) => {
                                setStatusFilter(e.target.value);
                                setCurrentPage(1);
                            }}
                        >
                            <option value="All">{t("brand.statusAll")}</option>
                            <option value="true">{t("brand.statusActive")}</option>
                            <option value="false">{t("brand.statusInactive")}</option>
                        </select>
                    </label>

                    <button className="category-clear-filters" type="button" onClick={clearFilters}>
                        <X size={16} aria-hidden="true" />
                        {t("brand.clearFilters")}
                    </button>
                </div>
            </section>

            <div className="category-card brand-table-card">
              <div className="table-responsive">
                <table className="modern-table brand-table">
                    <thead>
                        <tr>
                            <th>{t("brand.colSrNo")}</th>
                            <th>{t("brand.colBrandName")}</th>
                            <th>{t("brand.colBrandStatus")}</th>
                            <th>{t("brand.colStatus")}</th>
                            {canManageBrand && (<th>{t("brand.colAction")}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {brandList?.length > 0 ? (
                            brandList.map((item, index) => (
                                    <tr key={item?.id || index}>
                                        <td>{(currentPage - 1) * perPage + index + 1}.</td>
                                        <td className="cursor-pointer"
                                            onClick={() =>
                                                setSelectedBrandId(
                                                    selectedBrandId === item.id ? null : item.id
                                                )
                                            }
                                        >
                                            {item?.name}
                                        </td>
                                        <td>
                                            <div className={`status-badge ${statusClassMap[item?.status]}`}>
                                                <span>{item?.status}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <div className={`status-badge ${statusClassMap[item?.is_active]}`}>
                                                <span>{item.is_active ? t("brand.isActiveTrue") : t("brand.isActiveFalse")}</span>
                                            </div>
                                        </td>

                                        {canManageBrand && (
                                            <td>
                                                <div className="d-flex gap-1 align-items-center">
                                                    {canEditBrand && (
                                                        <i
                                                            className="cursor-pointer mx-2 far fa-edit edit"
                                                            onClick={() => setEditCategory(item)}
                                                        />
                                                    )}

                                                    {canToggleBrand && (
                                                        <button
                                                            type="button"
                                                            className={`category-status-toggle ${item?.is_active ? "active" : "inactive"}`}
                                                            aria-label={`${item?.is_active ? t("brand.statusActive") : t("brand.statusInactive")} - ${item?.name}`}
                                                            onClick={() => handleDelete(item)}
                                                        >
                                                            <span className="category-status-toggle-icon" aria-hidden="true">
                                                                {item?.is_active ? <Play size={11} fill="currentColor" /> : <Pause size={11} fill="currentColor" />}
                                                            </span>
                                                            {item?.is_active ? t("brand.statusActive") : t("brand.statusInactive")}
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        )}
                                    </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={canManageBrand ? 5 : 4}>
                                    <EmptyList name={t("brand.emptyListName")} />
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
              </div>
            </div>

            <div className="mt-3">
                {totalbrands > 0 && (
                    <CustomPagination
                        total={totalbrands}
                        onPageChange={(page, perPage) => {
                            setCurrentPage(page);
                            setPerPage(perPage);
                            fetchData();
                        }}
                        resetPage={resetPage}
                    />
                )}
            </div>
        </div>
    );
};

export default Brand;
